<template>
  <component
    :is="MainComponent"
    v-model="isVisible"
    :width="isMobile ? undefined : 360"
  >
    <v-card class="text--text">
      <div v-if="!isMobile">
        <div class="d-flex align-center px-6 py-4">
          <v-icon
            class="mr-6"
            color="icon-secondary"
            size="24"
            @click="isVisible = false"
          >
            ui-icon-close-not-a-circle
          </v-icon>
          <div class="text-h6 text--text">
            {{ t(title, l10nOptions) }}
          </div>
        </div>
        <v-divider />
      </div>
      <div
        class="px-4"
        :class="[isMobile ? 'py-6' : 'py-4']"
      >
        <div
          v-if="isMobile"
          class="d-flex align-center mb-2"
        >
          <v-icon
            v-if="!documents.fakeDocuments"
            color="success"
            size="24"
          >
            ui-icon-shield-checkmark-40
          </v-icon>
          <div
            class="text-h6"
            :class="documents.fakeDocuments ? 'text--text' : 'text-success--text ml-4'"
          >
            {{ t(title, l10nOptions) }}
          </div>
        </div>
        <div class="text-body-1 text-secondary--text mb-2">
          <template v-if="documents.fakeDocuments">
            <!-- eslint-disable max-len -->
            {{ t('Мы отправили запрос в ответственное учреждение и получили ответ, что документ не выдавался:', l10nOptions) }}
          </template>
          <template v-else-if="isTypeLicense">
            {{ t('Лицензия клиники:', l10nOptions) }}
            <br>{{ documents.licenseNumber }}
          </template>
          <template v-else>
            {{ t('Специалист предоставил документы, а мы проверили их достоверность', l10nOptions) }}
          </template>
        </div>
        <template v-if="documents.fakeDocuments">
          <div
            v-for="(fakeDocument, i) in documents.fakeDocuments"
            :key="i"
            class="d-flex py-3"
          >
            <v-icon
              color="text-error"
              size="24"
            >
              ui-icon-circle-warning
            </v-icon>
            <div class="text-body-1 ml-4">
              {{ t?.(fakeDocument, l10nOptions) || fakeDocument }}
            </div>
          </div>
        </template>
        <template v-else>
          <div
            v-if="documents.isEducationConfirmed"
            class="d-flex py-3"
          >
            <v-icon
              color="text-success"
              size="24"
            >
              ui-icon-check-circle
            </v-icon>
            <div class="text-body-1 ml-4">
              {{ t('Диплом о базовом образовании', l10nOptions) }}
            </div>
          </div>
          <div
            v-if="documents.isCategoryConfirmed"
            class="d-flex py-2"
          >
            <v-icon
              v-if="documents.isCategoryValid && !documents.isCategoryExpiringSoon"
              color="text-success"
              size="24"
            >
              ui-icon-check-circle
            </v-icon>
            <v-icon
              v-else
              color="text-error"
              size="24"
            >
              ui-icon-circle-warning
            </v-icon>
            <div class="ml-4">
              <div class="text-body-1">
                {{ t('Удостоверение о категории', l10nOptions) }}
              </div>
              <div
                v-if="!documents.isCategoryValid"
                class="text-body-2 text-error--text"
              >
                {{ t('Документ уже недействителен', l10nOptions) }}
              </div>
              <div
                v-else-if="documents.categoryExpiresDate"
                class="text-body-2"
                :class="documents.isCategoryExpiringSoon ? 'text-error--text' : 'text-secondary--text'"
              >
                {{ t('Действительно до', l10nOptions) }} {{ categoryExpiresDateFormatted }}
              </div>
            </div>
          </div>
          <div
            v-if="documents.isScienceConfirmed"
            class="d-flex py-3"
          >
            <v-icon
              color="text-success"
              size="24"
            >
              ui-icon-check-circle
            </v-icon>
            <div class="text-body-1 ml-4">
              {{ translatedScienceText }}
            </div>
          </div>
        </template>
        <v-btn
          class="primary--text mt-3 branding-btn branding-btn--background"
          color="bg-primary"
          depressed
          block
          @click="handleClickButton"
        >
          {{ documentButtonText }}
        </v-btn>
      </div>
    </v-card>
  </component>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import I18NextVue from 'i18next'
import { format } from 'date-fns'
import { VBottomSheet, VDialog } from 'vuetify/lib'

const { t, language } = I18NextVue
const l10nOptions = { ns: language }

type Documents = {
  linkToDocuments?: string | null
  isEducationConfirmed?: boolean
  isCategoryConfirmed?: boolean
  categoryExpiresDate?: string
  isCategoryExpiringSoon?: boolean | null
  isCategoryValid?: boolean | null
  isScienceConfirmed?: boolean
  scienceDocumentType?: 1 | 2 | 3
  licenseNumber?: string
  fakeDocuments?: string[]
}

const isMobile = window.MOBILE_VERSION
const MainComponent = isMobile ? VBottomSheet : VDialog

const isVisible = ref(false)
const documents = ref<Documents>({})

const isTypeLicense = computed(() => Boolean(documents.value.licenseNumber))
const scienceText = computed(() => (
  documents.value.scienceDocumentType
    ? {
      1: 'Диплом кандидата наук',
      2: 'Диплом доктора наук',
      3: 'Аттестат профессора',
    }[documents.value.scienceDocumentType]
    : ''))
const translatedScienceText = computed(() => t(scienceText.value, { ns: language }))

const title = computed(() => {
  if (documents.value.fakeDocuments) {
    return 'Факт выдачи части документов опровергнут'
  }

  return isTypeLicense.value ? 'Лицензия проверена' : 'Документы проверены'
})

const categoryExpiresDateFormatted = computed(() => {
  if (!documents.value.categoryExpiresDate) {
    return ''
  }

  return format(new Date(documents.value.categoryExpiresDate), 'dd.MM.yy')
})

const documentButtonText = computed(() => {
  const text = documents.value.linkToDocuments ? 'Посмотреть документы' : 'Хорошо'

  return t(text, l10nOptions)
})

onMounted(() => {
  // eslint-disable-next-line no-undef
  window.addEventListener('documents-verified:open', ({ detail: payload }: CustomEventInit<Documents>) => {
    if (payload) {
      isVisible.value = true
      documents.value = payload
    }
  })
})

function handleClickButton() {
  isVisible.value = false

  if (documents.value.linkToDocuments) {
    window.location.assign(documents.value.linkToDocuments)
  }
}
</script>
